import type { EstimatorInput, EstimatorResult, ProjectType } from "@reno-estimator/core";
import { getQuestionSetForProjectType } from "@reno-estimator/config";
import type { EstimatorClient } from "./types.js";
import { mapAnswersToEstimatorInput, type RawAnswerBag } from "./input-mapper.js";

/**
 * Framework-agnostic controller for the question flow.
 *
 * Deliberately has no rendering code - a React, Vue, or vanilla-JS view
 * layer sits on top of this and only ever calls these methods and reads
 * this state. The controller has no pricing logic of its own; it delegates
 * to the injected EstimatorClient. This is the seam that keeps business
 * logic out of UI components per the project's core architecture
 * principle.
 */
export class EstimatorController {
  private answers: RawAnswerBag = {};
  private lastInput: EstimatorInput | undefined;

  constructor(
    private readonly projectType: ProjectType,
    private readonly client: EstimatorClient,
    private readonly requestIdFactory: () => string = () => crypto.randomUUID()
  ) {}

  getQuestions() {
    return getQuestionSetForProjectType(this.projectType);
  }

  setAnswer(questionId: string, value: unknown): void {
    this.answers[questionId] = value;
  }

  /** Removes a previously-set answer entirely (as opposed to setting it to undefined), used when a question's controlling answer changes and the question is no longer displayed. */
  clearAnswer(questionId: string): void {
    delete this.answers[questionId];
  }

  getAnswer(questionId: string): unknown {
    return this.answers[questionId];
  }

  getAnswers(): RawAnswerBag {
    return { ...this.answers };
  }

  async submit(): Promise<EstimatorResult> {
    const input = mapAnswersToEstimatorInput({
      requestId: this.requestIdFactory(),
      projectType: this.projectType,
      answers: this.answers
    });
    this.lastInput = input;
    return this.client.submit(input);
  }

  /** The EstimatorInput built by the most recent submit() call, if any - used by lead capture to attach the exact input that produced the visible estimate. */
  getLastInput(): EstimatorInput | undefined {
    return this.lastInput;
  }
}
